import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/layout/PublicHeader';
import { PublicFooter } from '../components/layout/PublicFooter';
import { apiService } from '../services/api';

export const ContactSupport: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        topic: 'General Inquiry',
        priority: 'Medium',
        message: ''
    });
    const [file, setFile] = useState<File | null>(null);
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.id]: e.target.value
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        setErrorMessage('');

        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('email', formData.email);
            data.append('subject', `[${formData.priority}] ${formData.topic}`);
            data.append('message', formData.message);
            if (file) {
                data.append('attachment', file);
            }

            await apiService.post('/contact/', data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setStatus('success');
            setFormData({ name: '', email: '', topic: 'General Inquiry', priority: 'Medium', message: '' });
            setFile(null);
        } catch (error) {
            console.error(error);
            setStatus('error');
            setErrorMessage('Failed to submit request. Please try again later.');
        }
    };

    return (
        <div className="bg-background-light dark:bg-background-dark font-display text-slate-900 dark:text-white min-h-screen flex flex-col overflow-x-hidden transition-colors duration-200">
            {/* Top Navigation */}
            <PublicHeader />

            {/* Main Content Area */}
            <main className="flex-grow flex flex-col items-center justify-center py-10 px-4 sm:px-8 lg:px-20">
                <div className="w-full max-w-[1200px] grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">

                    {/* Left Column: Info & Contact Details */}
                    <div className="flex flex-col gap-8 justify-center">
                        {/* Heading Section */}
                        <div className="flex flex-col gap-4">
                            <span className="text-primary font-bold tracking-wider text-sm uppercase">Support Center</span>
                            <h1 className="text-4xl md:text-5xl font-black leading-tight tracking-[-0.033em]">
                                Support & <br />Assistance
                            </h1>
                            <p className="text-slate-500 dark:text-[#9da6b9] text-lg font-normal leading-relaxed max-w-md">
                                Our dedicated security team is standing by to help you secure your infrastructure. Reach out directly or submit a ticket.
                            </p>
                        </div>

                        {/* Contact Info List */}
                        <div className="flex flex-col gap-4 mt-4">
                            <h3 className="text-xl font-bold mb-2">Direct Contact</h3>

                            {/* Email Item */}
                            <div className="flex items-center gap-4 bg-white dark:bg-[#1a2332] border border-slate-200 dark:border-[#282e39] rounded-xl p-4 transition-transform hover:scale-[1.02]">
                                <div className="text-white flex items-center justify-center rounded-lg bg-primary/20 text-primary shrink-0 size-12">
                                    <span className="material-symbols-outlined">mail</span>
                                </div>
                                <div className="flex flex-col flex-1 overflow-hidden">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Email Us</p>
                                    <p className="text-base font-medium truncate">support@fortifytech.org.pe</p>
                                </div>
                                <button className="shrink-0 p-2 hover:bg-slate-100 dark:hover:bg-[#282e39] rounded-lg transition-colors text-slate-400 hover:text-primary">
                                    <span className="material-symbols-outlined text-[20px]">content_copy</span>
                                </button>
                            </div>

                            {/* Phone Item */}
                            <div className="flex items-center gap-4 bg-white dark:bg-[#1a2332] border border-slate-200 dark:border-[#282e39] rounded-xl p-4 transition-transform hover:scale-[1.02]">
                                <div className="text-white flex items-center justify-center rounded-lg bg-primary/20 text-primary shrink-0 size-12">
                                    <span className="material-symbols-outlined">call</span>
                                </div>
                                <div className="flex flex-col flex-1 overflow-hidden">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Call Us</p>
                                    <p className="text-base font-medium truncate">+51 993040815</p>
                                </div>
                                <button className="text-sm font-bold text-primary bg-primary/10 hover:bg-primary/20 px-4 py-2 rounded-lg transition-colors">
                                    Call Now
                                </button>
                            </div>

                            {/* HQ Location */}
                            <div className="flex items-center gap-4 bg-white dark:bg-[#1a2332] border border-slate-200 dark:border-[#282e39] rounded-xl p-4 transition-transform hover:scale-[1.02]">
                                <div className="text-white flex items-center justify-center rounded-lg bg-primary/20 text-primary shrink-0 size-12">
                                    <span className="material-symbols-outlined">location_on</span>
                                </div>
                                <div className="flex flex-col flex-1 overflow-hidden">
                                    <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">Headquarters</p>
                                    <p className="text-base font-medium truncate">Jr. Ramon Castilla 555 - San Hilrarión - Peru</p>
                                </div>
                                <a className="shrink-0 p-2 hover:bg-slate-100 dark:hover:bg-[#282e39] rounded-lg transition-colors text-slate-400 hover:text-primary" href="#">
                                    <span className="material-symbols-outlined text-[20px]">map</span>
                                </a>
                            </div>
                        </div>

                        {/* Self Help Links */}
                        <div className="flex gap-4 mt-4">
                            <a className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary font-medium transition-colors" href="#">
                                <span className="material-symbols-outlined text-[18px]">menu_book</span>
                                Documentation
                            </a>
                            <a className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-primary dark:hover:text-primary font-medium transition-colors" href="#">
                                <span className="material-symbols-outlined text-[18px]">help</span>
                                FAQ
                            </a>
                        </div>
                    </div>

                    {/* Right Column: Contact Form */}
                    <div className="flex flex-col justify-center">
                        <div className="bg-white dark:bg-[#1a2332] rounded-2xl p-6 md:p-8 shadow-xl border border-slate-200 dark:border-[#282e39] relative overflow-hidden">
                            {/* Decorational Gradient Glow */}
                            <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 rounded-full blur-[80px] pointer-events-none"></div>

                            <h2 className="text-2xl font-bold mb-6 relative z-10">Send a request</h2>

                            {status === 'success' ? (
                                <div className="bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center animate-fade-in">
                                    <div className="mx-auto flex items-center justify-center size-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
                                        <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-3xl">check_circle</span>
                                    </div>
                                    <h3 className="text-lg font-bold text-green-800 dark:text-green-200 mb-2">Request Sent!</h3>
                                    <p className="text-green-700 dark:text-green-300">
                                        Thank you for contacting us. We will get back to you shortly.
                                    </p>
                                    <button
                                        onClick={() => setStatus('idle')}
                                        className="mt-6 text-sm font-medium text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 underline"
                                    >
                                        Send another request
                                    </button>
                                </div>
                            ) : (
                                <form className="flex flex-col gap-5 relative z-10" onSubmit={handleSubmit}>
                                    {status === 'error' && (
                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
                                            {errorMessage}
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="name">Full Name</label>
                                            <input
                                                className="w-full bg-slate-50 dark:bg-[#111318] border border-slate-200 dark:border-[#282e39] rounded-lg h-12 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                id="name"
                                                placeholder="John Doe"
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="email">Work Email</label>
                                            <input
                                                className="w-full bg-slate-50 dark:bg-[#111318] border border-slate-200 dark:border-[#282e39] rounded-lg h-12 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                id="email"
                                                placeholder="john@company.com"
                                                type="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="topic">Topic</label>
                                            <div className="relative">
                                                <select
                                                    className="w-full bg-slate-50 dark:bg-[#111318] border border-slate-200 dark:border-[#282e39] rounded-lg h-12 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-slate-900 dark:text-white appearance-none cursor-pointer"
                                                    id="topic"
                                                    value={formData.topic}
                                                    onChange={handleChange}
                                                >
                                                    <option>General Inquiry</option>
                                                    <option>Technical Support</option>
                                                    <option>Billing Question</option>
                                                    <option>Security Report</option>
                                                </select>
                                                <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xl">expand_more</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="priority">Priority</label>
                                            <div className="relative">
                                                <select
                                                    className="w-full bg-slate-50 dark:bg-[#111318] border border-slate-200 dark:border-[#282e39] rounded-lg h-12 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-slate-900 dark:text-white appearance-none cursor-pointer"
                                                    id="priority"
                                                    value={formData.priority}
                                                    onChange={handleChange}
                                                >
                                                    <option>Low</option>
                                                    <option>Medium</option>
                                                    <option>High</option>
                                                    <option>Critical</option>
                                                </select>
                                                <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xl">expand_more</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="message">Message</label>
                                        <textarea
                                            className="w-full bg-slate-50 dark:bg-[#111318] border border-slate-200 dark:border-[#282e39] rounded-lg p-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 resize-none"
                                            id="message"
                                            placeholder="Describe your issue..."
                                            rows={4}
                                            required
                                            value={formData.message}
                                            onChange={handleChange}
                                        ></textarea>
                                    </div>

                                    {/* File Upload Zone */}
                                    <div
                                        className="border-2 border-dashed border-slate-300 dark:border-[#282e39] rounded-lg p-6 flex flex-col items-center justify-center gap-2 text-center hover:bg-slate-50 dark:hover:bg-[#161c27] transition-colors cursor-pointer group relative"
                                        onClick={() => document.getElementById('file-upload')?.click()}
                                    >
                                        <input
                                            type="file"
                                            id="file-upload"
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />
                                        <span className="material-symbols-outlined text-3xl text-slate-400 group-hover:text-primary transition-colors">cloud_upload</span>
                                        <div className="text-xs text-slate-500 dark:text-slate-400">
                                            {file ? (
                                                <span className="text-primary font-bold">{file.name}</span>
                                            ) : (
                                                <>
                                                    <span className="text-primary font-bold hover:underline">Click to upload</span> or drag and drop
                                                    <br />SVG, PNG, JPG or GIF (max. 800x400px)
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 my-2">
                                        <input
                                            type="checkbox"
                                            id="agreement"
                                            className="w-5 h-5 rounded border-slate-400 text-primary focus:ring-primary"
                                            required
                                        />
                                        <label htmlFor="agreement" className="text-xs text-slate-500 dark:text-slate-400">
                                            I agree to the <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>.
                                        </label>
                                    </div>

                                    <button
                                        className="w-full h-12 bg-primary hover:bg-blue-600 text-white font-bold rounded-lg shadow-lg shadow-primary/30 transition-all active:scale-[0.98] flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                                        type="submit"
                                        disabled={status === 'submitting'}
                                    >
                                        {status === 'submitting' ? (
                                            <>
                                                <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                Submit Request
                                                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
                                            </>
                                        )}
                                    </button>

                                    <p className="text-center text-xs text-slate-500 dark:text-slate-500 mt-2">
                                        Typical response time: <span className="font-medium text-slate-700 dark:text-slate-300">&lt; 2 hours</span> for critical issues.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <PublicFooter />
        </div>
    );
};
